import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { hoyISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function lunesDe(fechaISO) {
  const d = new Date(`${fechaISO}T12:00:00`);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
}

function addDays(fechaISO, n) {
  const d = new Date(`${fechaISO}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function fmtRango(desde, hasta) {
  try {
    const a = new Date(`${desde}T12:00:00`);
    const b = new Date(`${hasta}T12:00:00`);
    const opts = { day: "numeric", month: "long", year: "numeric" };
    return `${a.toLocaleDateString("es-ES", opts)} – ${b.toLocaleDateString("es-ES", opts)}`;
  } catch {
    return `${desde} – ${hasta}`;
  }
}

export default function ProyectosTimeline({ empleados = [] }) {
  const [lunes, setLunes] = useState(() => lunesDe(hoyISO()));
  const [usuarioId, setUsuarioId] = useState("todos");
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);

  const domingo = useMemo(() => addDays(lunes, 6), [lunes]);
  const horaIni = data?.hora_inicio_vista ?? 7;
  const horaFin = data?.hora_fin_vista ?? 20;
  const minutosVista = (horaFin - horaIni) * 60;
  const horas = useMemo(
    () => Array.from({ length: horaFin - horaIni + 1 }, (_, i) => horaIni + i),
    [horaIni, horaFin],
  );

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data: respuesta } = await api.get("/proyectos/timeline", {
        params: {
          desde: lunes,
          hasta: domingo,
          usuario_id: usuarioId === "todos" ? undefined : usuarioId,
        },
      });
      setData(respuesta);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, [lunes, domingo, usuarioId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const PIX_POR_MIN = 1.1;
  const altura = minutosVista * PIX_POR_MIN;

  return (
    <div className="space-y-4" data-testid="proyectos-timeline">
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-zinc-200 bg-white p-3 shadow-sm">
        <div className="flex items-center gap-1">
          <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => setLunes(addDays(lunes, -7))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => setLunes(lunesDe(hoyISO()))}>
            Hoy
          </Button>
          <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => setLunes(addDays(lunes, 7))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-sm font-medium text-zinc-800">{fmtRango(lunes, domingo)}</p>
        <div className="ml-auto flex items-center gap-2">
          <Label className="text-xs text-zinc-500">Trabajador</Label>
          <Select value={usuarioId} onValueChange={setUsuarioId}>
            <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              {empleados.map((u) => (
                <SelectItem key={u.id} value={String(u.id)}>
                  {u.nombre} {u.apellidos}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {cargando ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-violet-500" /></div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white shadow-sm">
          <div className="min-w-[900px]">
            <div className="grid grid-cols-[56px_repeat(7,minmax(0,1fr))] border-b border-zinc-100">
              <div className="bg-zinc-50" />
              {(data?.dias || []).map((d) => (
                <div key={d.fecha} className="border-l border-zinc-100 px-2 py-3 text-center">
                  <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{d.label}</p>
                  <p className="mt-0.5 text-sm font-medium text-zinc-800">{d.horas_label || "0m"}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-[56px_repeat(7,minmax(0,1fr))]">
              <div className="relative border-r border-zinc-100 bg-zinc-50/50" style={{ height: altura }}>
                {horas.map((h) => (
                  <div
                    key={h}
                    className="absolute right-2 -translate-y-1/2 text-[10px] font-medium text-zinc-400"
                    style={{ top: (h - horaIni) * 60 * PIX_POR_MIN }}
                  >
                    {String(h).padStart(2, "0")}:00
                  </div>
                ))}
              </div>

              {(data?.dias || []).map((d) => (
                <div key={d.fecha} className="relative border-l border-zinc-100" style={{ height: altura }}>
                  {horas.map((h) => (
                    <div
                      key={h}
                      className="absolute inset-x-0 border-t border-zinc-100"
                      style={{ top: (h - horaIni) * 60 * PIX_POR_MIN }}
                    />
                  ))}
                  {d.bloques.map((b, i) => {
                    const top = Math.max(0, (b.minutos_desde - horaIni * 60) * PIX_POR_MIN);
                    const height = Math.max(22, b.minutos_duracion * PIX_POR_MIN);
                    const titulo = b.tarea_nombre || b.proyecto_nombre;
                    return (
                      <div
                        key={`${b.inicio}-${i}`}
                        title={`${b.usuario}\n${b.proyecto_codigo} ${b.proyecto_nombre}${b.tarea_nombre ? ` · ${b.tarea_nombre}` : ""}\n${b.hora_inicio} – ${b.hora_fin} (${b.duracion})`}
                        className="absolute inset-x-1 overflow-hidden rounded-md px-1.5 py-1 text-white shadow-sm ring-1 ring-black/5"
                        style={{
                          top,
                          height,
                          backgroundColor: b.color,
                        }}
                      >
                        <p className="truncate text-[11px] font-semibold leading-tight">{titulo}</p>
                        <p className="truncate text-[10px] opacity-90">
                          {b.hora_inicio}–{b.hora_fin} · {b.duracion}
                        </p>
                        {usuarioId === "todos" && (
                          <p className="truncate text-[9px] opacity-80">{b.usuario}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
